import React, { useState, useEffect, useMemo } from "react";
import Menu from "../Menu";

const DQRImagesPreview = () => {
  const [dqrData, setDqrData] = useState(null);
  const [structures, setStructures] = useState([]); // full list from GET /Get/DqrId/{dqrid}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const token = getCookie("token") || getCookie("authToken");
  const userid = getCookie("userid") || getCookie("userId");

  // Extract dqrid from the URL via query (?dqrid=7) or path (/dqr-images-preview/7)
  const getDqridFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const fromQuery = searchParams.get("dqrid");
    if (fromQuery) return fromQuery;
    const parts = window.location.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    return /^\d+$/.test(last) ? last : null;
  };

  // ---- API: fetch DQR category + images ----
  const fetchDqrData = async () => {
    setLoading(true);
    setError(null);
    try {
      const dqrid = getDqridFromUrl();
      const response = await fetch(
        "https://api.dqr-display-tms.bonrix.in/api/DQRInfoOperation/SubUser/Search",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token,
          },
          body: JSON.stringify({
            dqrid,
            returntype: "structure",
            ispageload: false,
            pagesize: 1000,
            currentpageno: 0,
            withoutpagination: false,
            pagedirection: "first",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setDqrData(data);
    } catch (err) {
      setError("Failed to fetch DQR data: " + err.message);
      console.error("Error fetching DQR data:", err);
    } finally {
      setLoading(false);
    }
  };

  // ---- API: fetch ALL image structures for this DQR (GET /Get/DqrId/{dqrid}) ----
  const fetchAllStructuresForDqr = async () => {
    try {
      console.log("fetchAllStructuresForDqr121111111111");
      const dqrid = getDqridFromUrl();
      if (!dqrid) return;

      const url = `https://api.dqr-display-tms.bonrix.in/api/DQRImageStructureOperation/Get/DqrId/${dqrid}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          accept: "text/plain",
          UserId: userid,
          "X-Authorization": token,
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const json = await res.json();
      const rows = Array.isArray(json?.data) ? json.data : [];
      setStructures(rows);
    } catch (err) {
      console.error("Error fetching structures:", err);
      // (Keep UI usable even if this fails; we just won't show overlays)
      setStructures([]);
    }
  };

  // Build a lookup map from dqrcategoryid -> structure row
  const structureByCategoryId = useMemo(() => {
    const map = new Map();
    for (const row of structures) {
      if (row?.dqrcategoryid != null) {
        map.set(row.dqrcategoryid, row);
      }
    }
    return map;
  }, [structures]);

  // Initial load
  useEffect(() => {
    fetchDqrData();
    fetchAllStructuresForDqr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigation
  const nextSlide = () => {
    if (dqrData?.data?.categorydata) {
      setCurrentSlide((prev) =>
        prev === dqrData.data.categorydata.length - 1 ? 0 : prev + 1
      );
    }
  };
  const prevSlide = () => {
    if (dqrData?.data?.categorydata) {
      setCurrentSlide((prev) =>
        prev === 0 ? dqrData.data.categorydata.length - 1 : prev - 1
      );
    }
  };
  const goToSlide = (index) => setCurrentSlide(index);

  // Auto-play
  useEffect(() => {
    if (dqrData?.data?.categorydata && dqrData.data.categorydata.length > 1) {
      const t = setInterval(nextSlide, 5000);
      return () => clearInterval(t);
    }
  }, [dqrData, currentSlide]);

  // Render current slide with 320x480 stage (portrait)
  const renderCurrentSlide = () => {
    if (!dqrData?.data?.categorydata?.length) return null;

    const currentCategory = dqrData.data.categorydata[currentSlide];
    const imageData = currentCategory?.imagedata?.[0];
    if (!imageData) {
      return (
        <div className="slide-content">
          <div className="no-image">No image available</div>
        </div>
      );
    }

    // Prefer the structure row found by dqrcategoryid; fallback to the category
    const structureRow = structureByCategoryId.get(currentCategory.categoryid);
    const display = structureRow || currentCategory;

    // Normalize booleans
    const center =
      display?.center === true ||
      (typeof display?.center === "string" &&
        display.center.toLowerCase() === "true");

    const slideStyle = {
      position: "relative",
      width: "320px",
      height: "480px",
      backgroundImage: `url(${imageData.filepath})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto",
      border: "2px solid #ddd",
      borderRadius: "10px",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    };

    const textStyle = {
      position: "absolute",
      left:
        display?.textpositionx !== undefined && display?.textpositionx !== ""
          ? `${display.textpositionx}px`
          : "50%",
      top:
        display?.textpositiony !== undefined && display?.textpositiony !== ""
          ? `${display.textpositiony}px`
          : "50%",
      transform: center ? "translate(-50%, -50%)" : "none",
      fontFamily:
        display?.fontfamily && display.fontfamily !== "Select"
          ? display.fontfamily
          : "Arial",
      fontSize: display?.fontsize ? `${display.fontsize}px` : "20px",
      color: display?.fontcolor || "#000000",
      fontWeight: "bold",
      textAlign: center ? "center" : "left",
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      padding: "10px 15px",
      borderRadius: "5px",
      maxWidth: "300px",
      wordWrap: "break-word",
      zIndex: 10,
    };

    // Optional image/QR overlay placeholder based on structure (position + dimensions)
    const hasImageBox =
      display?.imagepositionx !== undefined && display?.imagepositionx !== "" &&
      display?.imagepositiony !== undefined && display?.imagepositiony !== "" &&
      display?.imagewidth !== undefined && display?.imagewidth !== "" &&
      display?.imageheight !== undefined && display?.imageheight !== "";

    const imageBoxStyle = hasImageBox
      ? {
          position: "absolute",
          left: `${display.imagepositionx}px`,
          top: `${display.imagepositiony}px`,
          width: `${display.imagewidth}px`,
          height: `${display.imageheight}px`,
          border: "2px dashed rgba(255,255,255,0.9)",
          borderRadius: "6px",
          backgroundColor: "rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: "12px",
          textAlign: "center",
          zIndex: 9,
        }
      : null;

    return (
      <div className="slide-content" style={slideStyle}>
        {hasImageBox && (
          <div style={imageBoxStyle}>
            {display?.addqr ? "QR/Img" : "Image Box"}
          </div>
        )}
        {display?.exampletext ? (
          <div style={textStyle}>{display.exampletext}</div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">DQR Images Preview</h3>
                <div className="card-tools">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={fetchDqrData}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Refresh123"}
                  </button>
                </div>
              </div>

              <div className="card-body">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {loading && (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}

                {!loading && dqrData?.data?.categorydata && (
                  <div className="slider-container">
                    {/* Main Slider */}
                    <div className="slider-wrapper">
                      <div className="slider-main">{renderCurrentSlide()}</div>

                      {/* Arrows */}
                      {dqrData.data.categorydata.length > 1 && (
                        <>
                          <button
                            className="slider-btn slider-btn-prev"
                            onClick={prevSlide}
                          >
                            &#8249;
                          </button>
                          <button
                            className="slider-btn slider-btn-next"
                            onClick={nextSlide}
                          >
                            &#8250;
                          </button>
                        </>
                      )}
                    </div>

                    {/* Indicators */}
                    {dqrData.data.categorydata.length > 1 && (
                      <div className="slider-indicators">
                        {dqrData.data.categorydata.map((_, index) => (
                          <button
                            key={index}
                            className={`indicator ${
                              index === currentSlide ? "active" : ""
                            }`}
                            onClick={() => goToSlide(index)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Info Panel */}
                    {dqrData.data.categorydata[currentSlide] && (
                      <div className="slide-info mt-3">
                        <h5>
                          Category:{" "}
                          {dqrData.data.categorydata[currentSlide].categoryname}
                        </h5>
                        <p>
                          DQR Name:{" "}
                          {dqrData.data.categorydata[currentSlide].dqrname}
                        </p>
                        <p>
                          Slide {currentSlide + 1} of{" "}
                          {dqrData.data.categorydata.length}
                        </p>

                        {/* Structure details (from GET /Get/DqrId/{dqrid}) */}
                        {(() => {
                          const cat =
                            dqrData.data.categorydata[currentSlide];
                          const row =
                            structureByCategoryId.get(cat.categoryid);
                          return row ? (
                            <div className="structure-info mt-2">
                              <h6>Image Structure Data:</h6>
                              <p>
                                <strong>Text:</strong> {row.exampletext}
                              </p>
                              <p>
                                <strong>Font:</strong> {row.fontfamily} (
                                {row.fontsize}px)
                              </p>
                              <p>
                                <strong>Color:</strong> {row.fontcolor}
                              </p>
                              <p>
                                <strong>Position:</strong> X: {row.textpositionx}
                                , Y: {row.textpositiony}
                              </p>
                              <p>
                                <strong>Center:</strong> {String(row.center)}
                              </p>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {!loading &&
                  (!dqrData?.data?.categorydata ||
                    dqrData.data.categorydata.length === 0) &&
                  !error && (
                    <div className="alert alert-info">No DQR data found.</div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .slider-container {
          position: relative;
          max-width: 100%;
          margin: 0 auto;
        }
        .slider-wrapper {
          position: relative;
          overflow: hidden;
          border-radius: 10px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        .slider-main {
          position: relative;
          width: 100%;
          height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .slide-content {
          width: 320px;
          height: 480px;
          position: relative;
          transition: all 0.3s ease;
        }
        .slide-content:hover {
          transform: scale(1.02);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
        }
        .no-image {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          background-color: #f8f9fa;
          color: #6c757d;
          font-size: 18px;
        }
        .slider-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          font-size: 24px;
          cursor: pointer;
          z-index: 10;
          transition: all 0.3s ease;
        }
        .slider-btn:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: translateY(-50%) scale(1.1);
        }
        .slider-btn-prev {
          left: 20px;
        }
        .slider-btn-next {
          right: 20px;
        }
        .slider-indicators {
          display: flex;
          justify-content: center;
          margin-top: 20px;
          gap: 10px;
        }
        .indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: none;
          background: #ccc;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .indicator.active {
          background: #007bff;
          transform: scale(1.2);
        }
        .indicator:hover {
          background: #007bff;
          transform: scale(1.1);
        }
        .slide-info {
          text-align: center;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 5px;
          margin-top: 20px;
        }
        .structure-info {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 5px;
          border-left: 4px solid #007bff;
          margin-top: 10px;
        }
      `}</style>
    </>
  );
};

export default DQRImagesPreview;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}
